<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    multiselect: {
      type: Boolean,
      required: false,
    },
    kind: {
      type: String,
      required: false,
    },
    company_id: {
      type: String,
      required: false,
    },
    client_id: {
      type: String,
      required: false,
    },
    invoice_number: {
      type: String,
      required: false,
    },
    invoice_date: {
      type: [String, Date],
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const invoicesGrid = ref(null);

  const $refs = {
    get invoicesGrid() {
      return invoicesGrid.value;
    },
  };

  const invoiceTypeBadgeClass = (...args) => {
    const [row] = args;
    const cc = $config.value.invoice.types.colorCodes[row.invoice_type];
    return `bg-${cc.bg} text-${cc.text}`;
  };

  const formId = ref(null);
  const selectedItems = ref(null);
  const invoices = ref([]);
  const selectedInvoices = ref([]);
  const initialCompany = ref(null);
  const initialClient = ref(null);
  const filters = ref(null);
  const invoiceKinds = ref([
    {
      id: 'incoming',
      text: $fn.value.pgettext('InvoiceKind', 'Incoming'),
    },
    {
      id: 'outgoing',
      text: $fn.value.pgettext('InvoiceKind', 'Outgoing'),
    },
  ]);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    emptyFooter: true,
    size: 'full-screen',
  }));
  const b_5731695935601903_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'own',
    },
  }));
  const b_3054425420812800_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
    },
  }));
  const b_4044930568678877_style = computed(() => ({
    flex: '1 1 0',
    'min-height': '0',
  }));
  const b_2070266589801014_showToolbar = computed(() => false);
  const b_2070266589801014_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2070266589801014_columns = computed(() => [
    {
      field: 'invoice_kind',
      name: $fn.value.pgettext('InvoicesView', 'Kind'),
      formatter: {
        name: 'mapvalue',
        mapping: {
          incoming: $fn.value.pgettext('InvoiceKind', 'In'),
          outgoing: $fn.value.pgettext('InvoiceKind', 'Out'),
        },
      },
    },
    {
      field: 'invoice_number',
      name: $fn.value.pgettext('InvoicesView', 'Invoice nr'),
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'template',
              engine: 'hb',
              format:
                '#!{{ $value }} {{~#if reference_number }} ({{ reference_number }}){{~/if }}',
            },
            {
              name: 'cellclass',
              value: (row) => ({
                'fw-bold': true,
                'bg-yellow-200': !row.locked,
                'bg-yellow-500 text-yellow-800': row.is_corrected,
                'bg-red-300 text-red-800': row.is_reversed,
              }),
            },
          ],
        },
      ],
    },
    {
      field: 'invoice_type',
      name: $fn.value.pgettext('InvoicesView', 'Type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'InvoiceType',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            dot: true,
            pill: true,
            badgeClass: invoiceTypeBadgeClass,
          },
        ],
      },
    },
    {
      field: 'invoice_status',
      name: $fn.value.pgettext('InvoicesView', 'Status'),
      formatter: {
        name: 'gettext',
        context: 'Status',
        conversion: 'StartCase',
        filterInput: true,
      },
    },
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('InvoicesView', 'Date'),
    },
    {
      field: 'fulfillment_date',
      name: $fn.value.pgettext('InvoicesView', 'Fulfillment date'),
      type: 'date',
    },
    {
      field: 'due_date',
      name: $fn.value.pgettext('InvoicesView', 'Due date'),
      type: 'date',
    },
    {
      field: 'register_entry',
      name: $fn.value.pgettext('InvoicesView', 'Register entry'),
      formatter: {
        name: 'getvalue',
        field: 'entry_number',
        source: 'value',
      },
    },
    {
      field: 'client_name',
      name: $fn.value.pgettext('InvoicesView', 'Client'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('InvoicesView', 'Description'),
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('InvoicesView', 'Net'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'total_amount',
      name: $fn.value.pgettext('InvoicesView', 'Amount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'outstanding_amounts',
      name: $fn.value.pgettext('Invoices', 'Outstanding Amounts'),
      type: 'number',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'number',
          },
          {
            name: 'cellclass',
            value: (row) => {
              if (row.flags && row.flags.no_payment_required) {
                return 'fw-bold text-decoration-line-through';
              }

              if (
                row.outstanding_amounts != null &&
                row.outstanding_amounts > 0
              ) {
                return 'fw-bold text-red-500';
              }

              if (
                row.outstanding_amounts != null &&
                row.outstanding_amounts < 0
              ) {
                return 'fw-bold text-blue-500';
              }

              if (row.outstanding_amounts == 0) {
                return 'fw-bold text-green-500';
              }
            },
          },
        ],
      },
    },
    {
      field: 'paid_amount',
      name: $fn.value.pgettext('InvoicesView', 'Paid'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'max_payment_date',
      name: $fn.value.pgettext('PaymentBrowserView', 'Date of last settlement'),
      type: 'date',
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('InvoicesView', 'Currency'),
    },
    {
      field: 'tags',
      name: $fn.value.pgettext('InvoicesView', 'Tags'),
    },
    {
      field: 'rentals',
      name: $fn.value.pgettext('InvoicesView', 'Rentals'),
      formatter: {
        name: 'chain',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'contracts',
      name: $fn.value.pgettext('InvoicesView', 'Contracts'),
      formatter: {
        name: 'chain',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_number',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    formId: formId.value,
    selectedItems: selectedItems.value,
    invoices: invoices.value,
    selectedInvoices: selectedInvoices.value,
    initialCompany: initialCompany.value,
    initialClient: initialClient.value,
    filters: filters.value,
    invoiceKinds: invoiceKinds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5731695935601903_dataSource: b_5731695935601903_dataSource.value,
    b_3054425420812800_dataSource: b_3054425420812800_dataSource.value,
    b_4044930568678877_style: b_4044930568678877_style.value,
    b_2070266589801014_showToolbar: b_2070266589801014_showToolbar.value,
    b_2070266589801014_columnDefaults: b_2070266589801014_columnDefaults.value,
    b_2070266589801014_columns: b_2070266589801014_columns.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_initialCompany = () => {
    return props.company_id
      ? $fn.value.fetch('read_company', {
          parameters: { id: props.company_id },
        })
      : null;
  };
  const $$load_initialClient = () => {
    return props.client_id
      ? $fn.value.fetch('read_company', { parameters: { id: props.client_id } })
      : null;
  };
  const $$load_filters = () => {
    return {
      date_from: props.invoice_date,
      date_to: null,
      kind: props.kind ? [props.kind] : [],
      company_id: props.company_id,
      client_id: props.client_id,
      unpaid: true,
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'selectedItems' },
      { name: 'invoices' },
      { name: 'selectedInvoices' },
      { name: 'initialCompany' },
      { name: 'initialClient' },
      { name: 'filters' },
      { name: 'invoiceKinds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5731695935601903_dataSource', nodeId: '5731695935601903' },
      { name: 'b_3054425420812800_dataSource', nodeId: '3054425420812800' },
      { name: 'b_4044930568678877_style', nodeId: '4044930568678877' },
      { name: 'b_2070266589801014_showToolbar', nodeId: '2070266589801014' },
      { name: 'b_2070266589801014_columnDefaults', nodeId: '2070266589801014' },
      { name: 'b_2070266589801014_columns', nodeId: '2070266589801014' },
    ],
    loaders: {
      formId: $$load_formId,
      initialCompany: $$load_initialCompany,
      initialClient: $$load_initialClient,
      filters: $$load_filters,
    },
    ux: {},
    dataRefs: {
      formId,
      selectedItems,
      invoices,
      selectedInvoices,
      initialCompany,
      initialClient,
      filters,
      invoiceKinds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5731695935601903_dataSource,
      b_3054425420812800_dataSource,
      b_4044930568678877_style,
      b_2070266589801014_showToolbar,
      b_2070266589801014_columnDefaults,
      b_2070266589801014_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('InvoiceBrowser', 'Browse Invoices') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.multiselect,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('multiselect');
    }
  );
  watch(
    () => props.kind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('kind');
    }
  );
  watch(
    () => props.company_id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('company_id');
    }
  );
  watch(
    () => props.client_id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('client_id');
    }
  );
  watch(
    () => props.invoice_number,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice_number');
    }
  );
  watch(
    () => props.invoice_date,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice_date');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_6icokppdb_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_5534025912102772_setData_f8zbaby6y_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'invoices',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5534025912102772_cache_clim3104z_getActionArgs = async ($event) => {
    return {
      path: 'filters',
    };
  };
  const act_5534025912102772_request_um026g1l5_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'list_invoice',
      parameters: _.pickBy(filters.value, (v) => v || !_.isEmpty(v)),
    };
  };
  const act_5534025912102772_setData_h0ronv6jd_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'invoices',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5534025912102772_cache_2pdo11i67_getActionArgs = async ($event) => {
    return {
      path: 'filters',
    };
  };
  const act_5534025912102772_request_lx2v7bfgb_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'list_invoice',
      parameters: _.pickBy(filters.value, (v) => v || !_.isEmpty(v)),
    };
  };
  const act_8595055453218999_closeComponent_lpzmm0vfk_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: props.multiselect ? selectedItems.value : selectedItems.value[0],
      },
    };
  };
  const act_2070266589801014_setData_ss6s59nzn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.invoicesGrid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_6icokppdb =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_6icokppdb_getActionArgs,
    });
  const inst_evh_7315092382398562_close_fcocj6y8u =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_6icokppdb,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_fcocj6y8u],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ij42qe45n =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_bxm2lgpyq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ij42qe45n,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_bxm2lgpyq],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_833pnqd3b =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_0042j77ex =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_833pnqd3b,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_0042j77ex],
    event: 'close',
  });
  const act_5534025912102772_setData_f8zbaby6y = new actionTypes.SetDataAction({
    actionArgs: act_5534025912102772_setData_f8zbaby6y_getActionArgs,
  });
  const inst_evh_5534025912102772_success_kwcoqeda4 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_setData_f8zbaby6y,
      event: 'success',
      displayName: 'setData',
    });
  const act_5534025912102772_cache_clim3104z = new actionTypes.CacheAction({
    actionArgs: act_5534025912102772_cache_clim3104z_getActionArgs,
  });
  const inst_evh_5534025912102772_success_wdt9q4vzo =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_cache_clim3104z,
      event: 'success',
      displayName: 'cache',
    });
  const act_5534025912102772_request_um026g1l5 = new actionTypes.RequestAction({
    actionArgs: act_5534025912102772_request_um026g1l5_getActionArgs,
    displayName: 'search',
    events: [
      inst_evh_5534025912102772_success_kwcoqeda4,
      inst_evh_5534025912102772_success_wdt9q4vzo,
    ],
  });
  const inst_evh_5534025912102772_submit_ouymn77ej =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_request_um026g1l5,
      event: 'submit',
      displayName: 'search',
    });
  const inst_evh_5534025912102772_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_submit_ouymn77ej],
    event: 'submit',
  });
  const act_5534025912102772_setData_h0ronv6jd = new actionTypes.SetDataAction({
    actionArgs: act_5534025912102772_setData_h0ronv6jd_getActionArgs,
  });
  const inst_evh_5534025912102772_success_6bk6r879t =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_setData_h0ronv6jd,
      event: 'success',
      displayName: 'setData',
    });
  const act_5534025912102772_cache_2pdo11i67 = new actionTypes.CacheAction({
    actionArgs: act_5534025912102772_cache_2pdo11i67_getActionArgs,
  });
  const inst_evh_5534025912102772_success_0ntnoqzle =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_cache_2pdo11i67,
      event: 'success',
      displayName: 'cache',
    });
  const act_5534025912102772_request_lx2v7bfgb = new actionTypes.RequestAction({
    actionArgs: act_5534025912102772_request_lx2v7bfgb_getActionArgs,
    displayName: 'search',
    events: [
      inst_evh_5534025912102772_success_6bk6r879t,
      inst_evh_5534025912102772_success_0ntnoqzle,
    ],
  });
  const inst_evh_5534025912102772_vueMounted_5nnutgd04 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_request_lx2v7bfgb,
      event: 'vue:mounted',
      displayName: 'search',
    });
  const inst_evh_5534025912102772_vueMounted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5534025912102772_vueMounted_5nnutgd04],
      event: 'vue:mounted',
    });
  const act_8595055453218999_closeComponent_lpzmm0vfk =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8595055453218999_closeComponent_lpzmm0vfk_getActionArgs,
    });
  const inst_evh_8595055453218999_click_b6ntun1t5 =
    new actionTypes.EventHandlerImpl({
      action: act_8595055453218999_closeComponent_lpzmm0vfk,
      event: 'click',
      displayName: 'closeComponent',
    });
  const inst_evh_8595055453218999_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8595055453218999_click_b6ntun1t5],
    event: 'click',
  });
  const act_2070266589801014_setData_ss6s59nzn = new actionTypes.SetDataAction({
    actionArgs: act_2070266589801014_setData_ss6s59nzn_getActionArgs,
  });
  const inst_evh_2070266589801014_selectedRowsChanged_ssrhvx9lt =
    new actionTypes.EventHandlerImpl({
      action: act_2070266589801014_setData_ss6s59nzn,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2070266589801014_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2070266589801014_selectedRowsChanged_ssrhvx9lt],
      event: 'selectedRowsChanged',
    });

  const actions = {
    inst_evh_7315092382398562_close_fcocj6y8u,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_bxm2lgpyq,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_0042j77ex,
    inst_evh_8564662037462133_close,
    inst_evh_5534025912102772_success_kwcoqeda4,
    inst_evh_5534025912102772_success_wdt9q4vzo,
    inst_evh_5534025912102772_submit_ouymn77ej,
    search: inst_evh_5534025912102772_submit_ouymn77ej,
    inst_evh_5534025912102772_submit,
    inst_evh_5534025912102772_success_6bk6r879t,
    inst_evh_5534025912102772_success_0ntnoqzle,
    inst_evh_5534025912102772_vueMounted_5nnutgd04,
    inst_evh_5534025912102772_vueMounted,
    inst_evh_8595055453218999_click_b6ntun1t5,
    inst_evh_8595055453218999_click,
    inst_evh_2070266589801014_selectedRowsChanged_ssrhvx9lt,
    inst_evh_2070266589801014_selectedRowsChanged,
  };

  const evh_7315092382398562_close_fcocj6y8u = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_fcocj6y8u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_bxm2lgpyq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_bxm2lgpyq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_0042j77ex = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_0042j77ex.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_kwcoqeda4 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_kwcoqeda4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_wdt9q4vzo = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_wdt9q4vzo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit_ouymn77ej = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit_ouymn77ej.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_6bk6r879t = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_6bk6r879t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_0ntnoqzle = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_0ntnoqzle.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_vueMounted_5nnutgd04 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_vueMounted_5nnutgd04.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_vueMounted = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_vueMounted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8595055453218999_click_b6ntun1t5 = async (event, scope) => {
    return await actions.inst_evh_8595055453218999_click_b6ntun1t5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8595055453218999_click = async (event, scope) => {
    return await actions.inst_evh_8595055453218999_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_selectedRowsChanged_ssrhvx9lt = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2070266589801014_selectedRowsChanged_ssrhvx9lt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2070266589801014_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoiceBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.invoice.color"
        :title="documentTitle"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" full-height v-if="loaded">
      <ItpBox direction="column" class="h-100 w-100 overflow-hidden">
        <ItpForm
          name="invoiceBrowser"
          :id="formId"
          class="h-auto flex-shrink-0"
          v-if="loaded"
          @submit="evh_5534025912102772_submit($event, {})"
          @vue:mounted="evh_5534025912102772_vueMounted($event, {})"
        >
          <div class="row align-items-center mb-3">
            <ItpFormSearchableSelect
              name="company"
              v-model="filters.company_id"
              :dataSource="b_5731695935601903_dataSource"
              :initialOptions="initialCompany ? [initialCompany] : []"
              valueField="id"
              textField="name"
              :placeholder="pgettext('InvoiceBrowser', 'Company')"
              class="col-4"
            >
            </ItpFormSearchableSelect>
            <ItpFormSearchableSelect
              name="kind"
              v-model="filters.kind"
              :options="invoiceKinds"
              valueField="id"
              textField="text"
              clearable
              :placeholder="pgettext('InvoiceBrowser', 'Invoice kind')"
              class="col-3"
            >
            </ItpFormSearchableSelect>
          </div>
          <div class="row align-items-center mb-3">
            <ItpFormSearchableSelect
              name="client"
              v-model="filters.client_id"
              :dataSource="b_3054425420812800_dataSource"
              :initialOptions="initialClient ? [initialClient] : []"
              valueField="id"
              textField="name"
              :placeholder="pgettext('InvoiceBrowser', 'Select client')"
              clearable
              optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
              use-option-template-for-selected-option
              class="col-4"
            >
            </ItpFormSearchableSelect>
            <div class="col-3 row">
              <ItpFormDatetime
                name="date_from"
                v-model="filters.date_from"
                :placeholder="pgettext('InvoiceBrowser', 'Date From')"
                class="col"
              >
              </ItpFormDatetime>
              <ItpFormDatetime
                name="date_to"
                v-model="filters.date_to"
                :placeholder="pgettext('InvoiceBrowser', 'Date To')"
                clearable
                class="col"
              >
              </ItpFormDatetime>
            </div>
            <div class="col align-items-center">
              <ItpFormCheckbox
                name="unpaid"
                v-model="filters.unpaid"
                :label="pgettext('InvoiceBrowser', 'Unpaid')"
                inline
                class="me-3"
              >
              </ItpFormCheckbox>
              <ItpButton
                type="submit"
                icon="fa-search"
                :text="gettext('Search')"
                size="md"
                variant="light"
                :spinning="actions.search.isRunning"
                :disabled="
                  !forms.invoiceBrowser || !forms.invoiceBrowser.flags.valid
                "
              >
              </ItpButton>
            </div>
          </div>
          <div class="row align-items-center mb-3">
            <div class="col align-items-center">
              <ItpButton
                icon="fa-check-square"
                :text="gettext('Add Selection')"
                size="md"
                variant="primary"
                :disabled="!selectedItems || !selectedItems.length"
                @click="evh_8595055453218999_click($event, {})"
              >
              </ItpButton>
            </div>
          </div>
        </ItpForm>
        <div
          class="position-relative overflow-hidden"
          :style="b_4044930568678877_style"
        >
          <ItpDataGrid
            name="invoiceBrowserInvoices"
            :items="invoices"
            checkbox-row-selection
            footer-totals
            :showToolbar="b_2070266589801014_showToolbar"
            :columnDefaults="b_2070266589801014_columnDefaults"
            :columns="b_2070266589801014_columns"
            class="position-absolute w-100 h-100"
            ref="invoicesGrid"
            @selectedRowsChanged="
              evh_2070266589801014_selectedRowsChanged($event, {})
            "
          >
          </ItpDataGrid>
        </div>
      </ItpBox>
    </UcBody>
  </ItpPage>
</template>
